import React, { useCallback, useContext, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { styled } from '@linaria/react';
import { HotelCardContext } from 'components/HotelCardContext';
import { useHotelCardActions } from 'hooks/useHotelCardActions';
import { getParametrizedHotelContext, getParametrizedHotelInfoContext } from 'middleware/analytics/selectors';
import { getIsAnchorHotel } from 'modules/sapiSearch/selectors';
import { toggle } from 'opticks';
import { SelectedElement, selectedElementToTabIdMapping } from 'utils/selectedElement';
import { generateAccommodationPageURL } from 'utils/url';
import { NoStyleLink } from '@daedalus/atlas/helpers/NoStyleLink';
import { getClosestDataIdToEventTarget } from '@daedalus/core/src/_web/utils/browser';
import { logTimeStart } from '@daedalus/core/src/_web/utils/logging/performanceTimer';
import { trackEvent } from '@daedalus/core/src/analytics/modules/actions';
import { Action, AnalyticsContext, Category, Entity } from '@daedalus/core/src/analytics/types/Events';
import { TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY } from '@daedalus/core/src/auth/modules/actions';
const ClickArea = /*#__PURE__*/styled('div')({
  name: "ClickArea",
  class: "coi0gwz",
  propsAsIs: false
});
interface Props {
  children: React.ReactElement;
}

/**
 * Wrapper component which creates a `<a>` link to the Accommodation Page
 * This is meant to be used internally on the hotel cards only
 */
export const AccommodationPageLink = ({
  children
}: Props) => {
  const {
    hotelId,
    isDummyLoadingCard,
    isHotelDetailsOverlay
  } = useContext(HotelCardContext);
  const {
    trackUserHotelCardClicked,
    onShowHotelDetails
  } = useHotelCardActions();
  const {
    search
  } = useLocation();
  const dispatch = useDispatch();
  const isAnchorHotel = useSelector(state => getIsAnchorHotel(state, hotelId));
  const accommodationPageURL = useMemo((): string => {
    return generateAccommodationPageURL(hotelId, search, isAnchorHotel);
  }, [search, hotelId, isAnchorHotel]);
  const handleOnContextMenu = useCallback(() => {
    dispatch(trackEvent({
      category: Category.System,
      entity: Entity.ContextMenu,
      action: Action.Displayed,
      component: 'ListViewHotelCard'
    }));
  }, [dispatch]);

  // Filter HeroImage and HeroImageCount as payload and pass Image instead
  const getSelectedElement = (e: React.MouseEvent<HTMLElement>) => {
    const dataId = getClosestDataIdToEventTarget(e);
    if (dataId) {
      return ['HeroImage', 'HeroImageCount'].includes(dataId) ? SelectedElement.Image : dataId;
    }
    return SelectedElement.BlankSpace;
  };
  const handleOnClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (isDummyLoadingCard || isHotelDetailsOverlay) return;
    const selectedElement = getSelectedElement(e);
    trackUserHotelCardClicked({
      hotelId: hotelId,
      component: 'ListViewHotelCard',
      selectedElement
    });
    logTimeStart('hotelCardClick');
    toggle('377fd843-desktop-ap--v2', () => {
      const tabId = selectedElementToTabIdMapping[selectedElement];
      onShowHotelDetails(hotelId, {
        [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
        [AnalyticsContext.HotelInfoContext]: getParametrizedHotelInfoContext(hotelId)
      }, tabId);
    }, () => {
      setTimeout(() => {
        window.open(accommodationPageURL, '_blank');
      }, TRACKING_TIMEOUT_BEFORE_NAVIGATING_AWAY);
    });
  }, [hotelId, onShowHotelDetails, trackUserHotelCardClicked, accommodationPageURL, isDummyLoadingCard, isHotelDetailsOverlay]);
  return toggle('377fd843-desktop-ap--v2', <ClickArea role="button" tabIndex={0} onClick={handleOnClick}>
      {children}
    </ClickArea>, <NoStyleLink href={accommodationPageURL} rel="noopener noreferrer" target="_blank" tabIndex={0} onClick={handleOnClick} onContextMenu={handleOnContextMenu}>
      {children}
    </NoStyleLink>);
};

require("./AccommodationPageLink.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./AccommodationPageLink.tsx");